'use strict';

const { filter, each, extend, uniq, map, min, sortBy } = require('underscore');

const BaseCollection = require('../collections/base');

const DatesCollection = require('../collections/dates');

const EventsCollection = require('../collections/events');

const SectionsCollection = require('../collections/sections');

const TimesCollection = require('../collections/times');

const EventsFilteredCollection = require('../filtered_collections/events');

const BaseModel = require('./base');

const QuantityModel = require('./quantity');

class BookerModel extends BaseModel {
  initialize() {
    try {
      if (!this.get('gliveAvailabilityHash') || !this.get('gliveAvailabilityHash').events) {
        throw new Error('Invalid availability data');
      }

      const eventsCollection = new EventsCollection(this.getEventsHash());
      const filteredEventsCollection = new EventsFilteredCollection(eventsCollection);
      const { isSoldOut } = this.get('gliveAvailabilityHash').product;
      const isAvailable = filteredEventsCollection.length > 0;
      const times = this.uniquifyAndSortTimes(filteredEventsCollection.getTimes());
      const dates = this.uniquifyAndSortDates(filteredEventsCollection.getDates());
      const sections = this.uniquifyAndSortPricesAndSections(filteredEventsCollection.getSections());
      const isTouch = this.get('isTouch');

      this.loaded = true;

      this.set({
        isSoldOut,
        isAvailable,
        isSellable: !isSoldOut && isAvailable,
        events: eventsCollection,
        filteredEvents: filteredEventsCollection,
        dates: new DatesCollection(dates),
        times: new TimesCollection(times),
        quantities: new BaseCollection(filteredEventsCollection.getQuantities(), { model: QuantityModel }),
        sections: new SectionsCollection(sections),
        bookClicked: false,
        requiresCaptcha: !!this.get('gliveAvailabilityHash').product.requiresCaptcha,
        captchaSiteKey: this.get('gliveAvailabilityHash').product.captchaSiteKey,
      });

      this.get('dates').meta('loaded', true);
      this.get('times').meta('loaded', true);

      this.get('events').meta('isTouch', isTouch);
      this.get('dates').meta('isTouch', isTouch);
      this.get('times').meta('isTouch', isTouch);
      this.get('quantities').meta('isTouch', isTouch);
      this.get('sections').meta('isTouch', isTouch);
    } catch (error) {
      if (error.message === 'Invalid availability data') {
        this.setError('loading');
      }
    }
  }

  getEventsHash() {
    let { events } = this.get('gliveAvailabilityHash');
    events = filter(events, event => !event.quantitySummary.isSoldOut);
    const discountEnabled = this.discountEnabled();
    each(events, event => extend(event, { discountEnabled }));

    return events;
  }

  setError(error) {
    this.set({ error });
  }

  clearError() {
    this.unset('error');
  }

  getError() {
    return this.get('error');
  }

  uniquifyAndSortTimes(timesArray) {
    const times = uniq(timesArray, time => time.toString());
    return sortBy(times, time => time.toString());
  }

  uniquifyAndSortDates(datesArray) {
    const dates = uniq(datesArray, date => date.toString());
    return sortBy(dates, date => date.toString());
  }

  uniquifyAndSortPricesAndSections(sectionsArray) {
    let sections = uniq(sectionsArray, section => section.getPriceTitle());
    sections = sortBy(sections, section => section.getTitle());
    sections = sortBy(sections, section => -section.getValue());
    sections = sortBy(sections, section => section.getPrice());
    sections = uniq(sections, section => section.getTitle());
    return sections;
  }

  getEvents() {
    return this.get('events');
  }

  requirePhone() {
    return false;
  }

  requireQuestion() {
    return false;
  }

  discountEnabled() {
    return this.get('deal').discountEnabled;
  }

  reset() {
    this.resetTime();
  }

  resetDate() {
    this.selectDate(null);
  }

  resetTime() {
    this.selectTime(null);
  }

  updateAllPickers() {
    const dates = this.uniquifyAndSortDates(this.get('filteredEvents').getDates());
    this.get('dates').reset(dates);

    const times = this.uniquifyAndSortTimes(this.get('filteredEvents').getTimes());
    this.get('times').reset(times);

    const quantities = this.get('filteredEvents').getQuantities();
    this.get('quantities').reset(quantities);

    const sections = this.uniquifyAndSortPricesAndSections(this.get('filteredEvents').getSections());
    this.get('sections').reset(sections);

    this.trigger('updatedCollections');
  }

  selectDate(date) {
    this.set({ selectedDate: date });
    this.get('filteredEvents').setDateFilter(date);
    this.updateAllPickers();
  }

  selectTime(time) {
    this.set({ selectedTime: time });
    this.get('filteredEvents').setTimeFilter(time);
    this.updateAllPickers();
  }

  selectSection(section) {
    this.set({ selectedSection: section });
    this.get('filteredEvents').setSectionFilter(section);
    this.updateAllPickers();
  }

  selectQuantity(quantity) {
    this.set({ selectedQuantity: quantity });
    this.get('filteredEvents').setQuantityFilter(quantity.getPartySize());
    this.updateAllPickers();
  }

  clearAll() {
    this.get('filteredEvents').resetFilters();
    this.updateAllPickers();
    this.set({
      selectedDate: null,
      selectedTime: null,
      selectedQuantity: null,
      selectedSection: null,
    });
    this.updateAllPickers();
    this.trigger('clearAll');
  }

  shouldShowCaptcha() {
    return (
      this.get('selectedDate') != null &&
      this.get('selectedTime') != null &&
      this.get('selectedQuantity') != null &&
      this.get('selectedSection') != null &&
      this.get('requiresCaptcha')
    );
  }

  showSpinner() {
    return this.get('showSpinner');
  }

  isReadyToBook() {
    let captchaResolved = true;
    if (this.get('requiresCaptcha')) {
      captchaResolved = !!window.grecaptcha.getResponse();
    }

    return (
      this.get('selectedDate') != null &&
      this.get('selectedTime') != null &&
      this.get('selectedQuantity') != null &&
      this.get('selectedSection') != null &&
      captchaResolved
    );
  }

  checkoutUrl() {
    const permalink = this.get('deal').id;
    const rootUrl = `/live/deals/${permalink}/reservation?`;

    const queries = {};
    const event = this.selectedEvent();
    queries.event = event.getUUID();
    queries.quantity = this.get('selectedQuantity').getPartySize();
    if (this.get('useNativeCheckout')) {
      queries.use_native_checkout = 'true';
    }
    if (this.get('requiresCaptcha')) {
      queries.captcha_token = window.grecaptcha.getResponse();
    }

    return rootUrl + map(queries, (v, k) => `${k}=${v}`).join('&');
  }

  selectedEvent() {
    return min(this.get('filteredEvents').toArray(), model => {
      if (model.getTierOrder() != null) {
        return model.getTierOrder();
      } else {
        return Infinity;
      }
    });
  }

  book() {
    if (!this.get('bookClicked')) {
      this.set({ bookClicked: true });
      this._setUrl(this.checkoutUrl());
    }
  }

  _setUrl(url) {
    window.location.href = url;
  }
}

module.exports = BookerModel;
